import cn from 'classnames';
import { FC, useState } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  useIntl,
} from 'react-intl';
import { generatePath, Link } from 'react-router';

import { TranscriptorEncounterResponse } from '@/shared/api/transcriptor/transcriptorApi';
import { PATHS } from '@/shared/config';
import { Dropdown, Icon } from '@/shared/ui';

import styles from '../../EncounterDetails.module.scss';
import { formatDurationFromNs } from '../../lib/formatDurationFromNs';
import { getEncounterStartDate } from '../../lib/getEncounterStartDate';
import { getEncounterTitle } from '../../lib/getEncounterTitle';
import { getFullName } from '../../lib/getFullName';

type EncounterDetailsHeaderProps = {
  encounter: TranscriptorEncounterResponse;
};

export const EncounterDetailsHeader: FC<EncounterDetailsHeaderProps> = (
  props,
) => {
  const { encounter } = props;
  const { formatMessage } = useIntl();
  const [isKebabOpen, setIsKebabOpen] = useState(false);
  const patientName = getFullName(encounter.patient_info);
  const staffName = getFullName(encounter.staff_info);
  const startedAt = getEncounterStartDate(encounter);
  const duration = formatDurationFromNs(encounter.total_audio_duration_ns);

  return (
    <header className={styles.header} data-component="encounter-details-header">
      <div className={styles.headerLeft}>
        <h2 className={styles.title}>{getEncounterTitle(encounter)}</h2>
        {patientName && (
          <p className={cn('p2', styles.subtitle)}>{patientName}</p>
        )}
      </div>

      <div className={cn('p3', styles.headerRight)}>
        {staffName && <span className={styles.metaItem}>{staffName}</span>}
        <span className={styles.metaItem}>
          <Icon name="calendar" size={16} />
          <span>
            <FormattedDate
              value={startedAt}
              day="numeric"
              month="short"
              year="numeric"
            />
            {', '}
            <FormattedTime value={startedAt} />
          </span>
        </span>
        {duration && (
          <span className={styles.metaItem}>
            <Icon name="time" size={16} />
            <span>{duration}</span>
          </span>
        )}
        <Dropdown
          position="bottom right"
          isOpen={isKebabOpen}
          setIsOpen={setIsKebabOpen}
          trigger={
            <button
              type="button"
              className={styles.kebabButton}
              aria-label={formatMessage({
                id: 'encounters.details.openMenu',
                defaultMessage: 'Open menu',
              })}
            >
              <Icon name="dots" size={20} />
            </button>
          }
        >
          {encounter.patient_id ? (
            <div className={styles.kebabMenu}>
              <Link
                to={generatePath(PATHS.patient, {
                  patientID: encounter.patient_id,
                })}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.kebabMenuItem}
                onClick={() => setIsKebabOpen(false)}
              >
                <FormattedMessage
                  id="encounters.details.goToPatientProfile"
                  defaultMessage="Go to patient profile"
                />
              </Link>
            </div>
          ) : (
            <div className={styles.kebabMenuPlaceholder}>
              <FormattedMessage
                id="encounters.details.menuPlaceholder"
                defaultMessage="No actions available yet"
              />
            </div>
          )}
        </Dropdown>
      </div>
    </header>
  );
};
